package org.moxultimate.topwithmox.showscrud.add

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.crud.CRUDDropZone
import org.moxultimate.topwithmox.components.crud.CRUDDropdown
import org.moxultimate.topwithmox.components.crud.CRUDFormField
import org.moxultimate.topwithmox.components.crud.CRUDSuccessDialog
import org.moxultimate.topwithmox.components.crud.crudShowTypes
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.utils.autoTagSync
import org.moxultimate.topwithmox.showscrud.main.ShowsCRUDState
import org.moxultimate.topwithmox.showscrud.main.ShowsCRUDViewModel
import org.moxultimate.topwithmox.showscrud.main.ShowsFormEntry
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * ShowsAddPage
 * Full-page form for adding a new show entry.
 * Thumbnail image drop zone + text fields for all show metadata.
 * On success, the user is returned to the shows CRUD list.
 */
@Composable
fun ShowsAddPage() {
    val viewModel: ShowsCRUDViewModel = koinViewModel()
    val crudState by viewModel.crudState.collectAsState()
    val navigator = rememberAppNavigator()

    var entry by remember { mutableStateOf(ShowsFormEntry()) }
    var submitAttempted by remember { mutableStateOf(false) }

    if (crudState is ShowsCRUDState.Success) {
        CRUDSuccessDialog(
            message = (crudState as ShowsCRUDState.Success).message,
            onOkay = {
                viewModel.resetState()
                navigator.goToShowsCRUD()
            },
        )
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .width(clamp(min = 900.px, value = 80.vw, max = 1400.px))
                .padding(top = 80.px, bottom = 32.px, leftRight = 2.cssRem),
            verticalArrangement = Arrangement.spacedBy(24.px),
        ) {
            // Page title
            SpanText(
                text = "Add Show / Series",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(32.px)
                    .fontWeight(FontWeight.Bold)
                    .moxTextGradient(),
            )

            // Error banner
            if (crudState is ShowsCRUDState.Error) {
                val error = crudState as ShowsCRUDState.Error
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 16.px, topBottom = 12.px)
                        .borderRadius(12.px)
                        .backgroundColor(Colors.MistyRose)
                        .border(1.px, LineStyle.Solid, Colors.LightCoral),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        text = "Error: ${error.message}",
                        modifier = Modifier.fontFamily(FontFamily).fontSize(13.px).color(Colors.Crimson),
                    )
                    SpanText(
                        text = "✕",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(14.px)
                            .color(Colors.Gray)
                            .onClick { viewModel.resetState() },
                    )
                }
            }

            // Validation hint
            if (submitAttempted && !entry.isValid) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 12.px, topBottom = 8.px)
                        .borderRadius(8.px)
                        .backgroundColor(Colors.MistyRose)
                        .border(1.px, LineStyle.Solid, Colors.LightCoral),
                ) {
                    SpanText(
                        text = "Missing required: ${entry.missingFields.joinToString(" · ")}",
                        modifier = Modifier.fontFamily(FontFamily).fontSize(12.px).color(Colors.Crimson),
                    )
                }
            }

            // Thumbnail image drop zone — full width
            CRUDDropZone(
                label = "Thumbnail Image *",
                file = entry.imageFile,
                fullWidth = true,
                onFileDropped = { entry = entry.copy(imageFile = it) },
                onFileRemoved = { entry = entry.copy(imageFile = null) },
            )

            // Title + Type
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
            ) {
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Title *",
                        value = entry.title,
                        placeholder = "e.g. Attack on Titan",
                        onValueChange = { entry = entry.copy(title = it, tags = autoTagSync(entry.tags, entry.title, it)) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDDropdown(
                        label = "Type *",
                        options = crudShowTypes,
                        selected = entry.type,
                        placeholder = "Select type…",
                        onSelected = { entry = entry.copy(type = it, tags = autoTagSync(entry.tags, entry.type, it)) },
                    )
                }
            }

            // Tags
            CRUDFormField(
                label = "Tags",
                value = entry.tags,
                placeholder = "Pipe-separated tags e.g. Action | Drama (optional)",
                onValueChange = { entry = entry.copy(tags = it) },
            )

            // Description + Video Link
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
            ) {
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Description",
                        value = entry.description,
                        placeholder = "Short description of the show",
                        onValueChange = { entry = entry.copy(description = it) },
                    )
                }
                Box(modifier = Modifier.weight(1f)) {
                    CRUDFormField(
                        label = "Video Link",
                        value = entry.videoLink,
                        placeholder = "YouTube or external video URL (optional)",
                        onValueChange = { entry = entry.copy(videoLink = it) },
                    )
                }
            }

            // Submit button
            val isLoading = crudState is ShowsCRUDState.Loading
            GradientButton(
                text = if (isLoading) "Uploading…" else "Create Show",
                disabled = isLoading,
                onClick = {
                    submitAttempted = true
                    if (entry.isValid && !isLoading) viewModel.createShow(entry)
                },
            )
        }
    }
}
